"use client";
import { gsap } from "gsap";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Briefcase, Calendar, Users, Globe } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const LogoParticles = ({ logoRef }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const particles = useRef([]);
  const started = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const spawnParticle = () => {
      const logo = logoRef.current;
      if (!logo) return;
      const lRect = logo.getBoundingClientRect();
      const cRect = canvas.getBoundingClientRect();
      const x = lRect.left - cRect.left + Math.random() * lRect.width;
      const y = lRect.top - cRect.top + lRect.height * 0.4;
      particles.current.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 0.2,
        vy: -(0.18 + Math.random() * 0.45),
        r: 1.2 + Math.random() * 2.2,
        alpha: 0.7 + Math.random() * 0.3,
        life: 0,
        maxLife: 80 + Math.random() * 80,
        hue: Math.random() > 0.5 ? `0, 210, 255` : `160, 100, 255`,
      });
    };

    let frameCount = 0;
    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frameCount++;
      if (started.current && frameCount % 5 === 0) {
        spawnParticle();
        if (Math.random() > 0.5) spawnParticle();
      }
      particles.current = particles.current.filter((p) => p.life < p.maxLife);
      for (const p of particles.current) {
        p.life++;
        p.x += p.vx;
        p.y += p.vy;
        p.vx += (Math.random() - 0.5) * 0.02;
        const progress = p.life / p.maxLife;
        const fade = progress < 0.2 ? progress / 0.2 : 1 - (progress - 0.2) / 0.8;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * (1 - progress * 0.4), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.hue}, ${p.alpha * fade})`;
        ctx.fill();
      }
    };

    animate();
    const timer = setTimeout(() => {
      started.current = true;
    }, 2200);

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(timer);
      window.removeEventListener("resize", resize);
    };
  }, [logoRef]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 29 }} />;
};

const STATS = [
  {
    value: "300+",
    label: "Projects Delivered",
    icon: Briefcase,
    color: "text-purple-600 dark:text-purple-400",
    bg: "bg-purple-500/10",
    border: "border-border-primary/50 hover:border-purple-500/40 dark:border-white/5",
    shadow: "hover:shadow-[0_0_25px_rgba(168,85,247,0.15)]",
    iconGlow: "shadow-[0_0_15px_rgba(168,85,247,0.3)]",
  },
  {
    value: "10+",
    label: "Years of Excellence",
    icon: Calendar,
    color: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-border-primary/50 hover:border-blue-500/40 dark:border-white/5",
    shadow: "hover:shadow-[0_0_25px_rgba(59,130,246,0.15)]",
    iconGlow: "shadow-[0_0_15px_rgba(59,130,246,0.3)]",
  },
  {
    value: "100+",
    label: "Happy Clients",
    icon: Users,
    color: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-border-primary/50 hover:border-emerald-500/40 dark:border-white/5",
    shadow: "hover:shadow-[0_0_25px_rgba(16,185,129,0.15)]",
    iconGlow: "shadow-[0_0_15px_rgba(16,185,129,0.3)]",
  },
  {
    value: "3",
    label: "Global Presence",
    icon: Globe,
    color: "text-pink-600 dark:text-pink-400",
    bg: "bg-pink-500/10",
    border: "border-border-primary/50 hover:border-pink-500/40 dark:border-white/5",
    shadow: "hover:shadow-[0_0_25px_rgba(236,72,153,0.15)]",
    iconGlow: "shadow-[0_0_15px_rgba(236,72,153,0.3)]",
  },
];

const StatCard = ({ item, extraClass = "" }) => {
  const Icon = item.icon;
  return (
    <div
      className={`hero-pill flex flex-col justify-center items-start p-5 rounded-2xl border ${item.border} backdrop-blur-xl transition-colors duration-500 ${item.shadow} group cursor-default relative overflow-hidden ${extraClass}`}
      style={{
        background: "var(--card-background)",
        boxShadow: "var(--card-shadow)",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="flex items-center gap-2 md:gap-3 mb-4 relative z-10">
        <div className={`p-2.5 rounded-full ${item.bg} ${item.color} ${item.iconGlow} group-hover:scale-110 transition-transform duration-500`}>
          <Icon size={20} strokeWidth={2.5} />
        </div>
        <div className="text-xl md:text-3xl font-bold text-text-primary">{item.value}</div>
      </div>
      <div className="text-[12px] md:text-sm text-text-secondary transition-colors font-medium relative z-10">{item.label}</div>
    </div>
  );
};

const AboutUsHero = () => {
  const root = useRef(null);
  const imageRef = useRef(null);
  const logoRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from(".hero-eyebrow", { y: 30, opacity: 0, duration: 0.8 })
        .from(".hero-line", { y: 80, opacity: 0, duration: 1.1, stagger: 0.12 }, "-=0.5")
        .from(".hero-sub", { y: 40, opacity: 0, duration: 1 }, "-=0.6")
        .from(".hero-pill", { scale: 0.85, opacity: 0, duration: 0.6, stagger: 0.1 }, "-=0.7")
        .from(".hero-cta", { y: 30, opacity: 0, duration: 0.7 }, "-=0.4")
        .from(".hero-scroll", { opacity: 0, y: 20, duration: 0.8 }, "-=0.3");

      if (imageRef.current) {
        gsap.to(imageRef.current, {
          yPercent: 18,
          ease: "none",
          scrollTrigger: { trigger: root.current, start: "top top", end: "bottom top", scrub: 1 },
        });
      }

      if (window.innerWidth >= 768) {
        gsap.set(logoRef.current, { y: -480, opacity: 0, scale: 1.2, rotation: -8 });

        const logoTl = gsap.timeline({ delay: 0.5 });
        logoTl
          .to(logoRef.current, { y: 0, opacity: 1, scale: 1, rotation: 0, duration: 0.7, ease: "power4.in" })
          .to(logoRef.current, { scaleX: 1.18, scaleY: 0.82, duration: 0.08, ease: "power2.out" })
          .to(logoRef.current, { scaleX: 1, scaleY: 1, duration: 0.14, ease: "elastic.out(1.5, 0.4)" })
          .to(logoRef.current, { y: -70, scaleX: 0.96, scaleY: 1.04, duration: 0.3, ease: "power2.out" })
          .to(logoRef.current, { y: 0, scaleX: 1.1, scaleY: 0.9, duration: 0.24, ease: "power2.in" })
          .to(logoRef.current, { scaleX: 1, scaleY: 1, duration: 0.12, ease: "elastic.out(1.2, 0.5)" })
          .to(logoRef.current, { y: -28, duration: 0.2, ease: "power2.out" })
          .to(logoRef.current, { y: 0, scaleX: 1.05, scaleY: 0.96, duration: 0.16, ease: "power2.in" })
          .to(logoRef.current, { scaleX: 1, scaleY: 1, duration: 0.1, ease: "elastic.out(1, 0.5)" })
          .to(logoRef.current, { filter: "drop-shadow(0 0 32px rgba(0,210,255,0.9)) drop-shadow(0 0 16px rgba(160,100,255,0.6))", duration: 0.3, ease: "power1.out" })
          .to(logoRef.current, { filter: "drop-shadow(0 0 18px rgba(0,210,255,0.5)) drop-shadow(0 0 8px rgba(160,100,255,0.3))", duration: 0.9, ease: "power2.inOut" })
          .to(logoRef.current, { y: -10, duration: 2.2, ease: "sine.inOut", repeat: -1, yoyo: true });

        gsap.to(logoRef.current, { rotation: 1.5, duration: 3.8, ease: "sine.inOut", repeat: -1, yoyo: true, delay: 2.2 });
        gsap.to(logoRef.current, {
          filter: "drop-shadow(0 0 24px rgba(0,210,255,0.65)) drop-shadow(0 0 12px rgba(160,100,255,0.45))",
          duration: 2.6,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
          delay: 2.2,
        });
      }
    }, root);

    return () => ctx.revert();
  }, []);
  return (
    <section ref={root} className="relative w-full overflow-hidden bg-background-primary">
      <style>{`

        @media (max-width: 767px) {
          .hero-pill {
            box-shadow: none !important;
          }
          .hero-pill:hover {
            box-shadow: none !important;
          }
        }
        .about-logo {
          display: none; 
        }
        @media (min-width: 768px) {
          .about-logo {
            display: block;
            position: absolute;
            right: 17%;
            top: auto;
            bottom: 24%;
            width: clamp(150px, 22vw, 250px);
            z-index: 30;
          }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .about-logo {
            display: block;
            position: absolute;
            right: 13.25%;
            bottom: 22%;
            width: clamp(140px, 18vw, 220px);
            z-index: 30;
          }
        }
        
        @media (min-width: 1080px) {
          .about-logo {
            right: 18.25%;
            bottom: 25%;
            width: clamp(200px, 20vw, 300px);
          }
        }
        @media (min-width: 1500px) {
          .about-logo {
            right: 17.4%;
            bottom: 10%;
            width: clamp(220px, 22vw, 400px);
          }
        }
      `}</style>

      <div ref={imageRef} className="hidden md:block absolute inset-0 w-full h-full top-0" style={{ zIndex: 1 }}>
        <Image src="/images/serviceBanners/aboutUsWhite.webp" alt="success stories" fill priority className="object-cover dark:hidden" />
        <Image src="/images/serviceBanners/aboutUsBlack.webp" alt="success stories" fill priority className="object-cover hidden dark:block" />
      </div>

      <div className="hidden md:block absolute inset-0 bg-gradient-to-t from-background-primary/40 via-transparent to-background-primary/20" style={{ zIndex: 2 }} />

      <div className="hidden md:block">
        <LogoParticles logoRef={logoRef} />
      </div>

      <div
        ref={logoRef}
        className="about-logo"
        style={{
          filter: "drop-shadow(0 0 18px rgba(0,210,255,0.5)) drop-shadow(0 0 8px rgba(160,100,255,0.3))",
          pointerEvents: "none",
          willChange: "transform, opacity, filter",
          transformOrigin: "center bottom",
        }}
      >
        <Image src="/images/Tycho_Logo_new.png" alt="Tycho Logo" width={400} height={300} style={{ width: "100%", height: "auto", objectFit: "contain" }} priority />
      </div>

      <div className="block md:hidden" style={{ position: "relative", zIndex: 3 }}>
        <div className="relative !px-6 " style={{ minHeight: "300px" }}>
          <div className="flex items-center gap-4 mb-4!">
            <h3 className="text-xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 !pt-2">
              About Us
            </h3>
            <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 rounded-full" />
          </div>
          <h1 className="hero-eyebrow font-display font-bold leading-[1.15] text-text-primary mb-4 text-[clamp(2rem,9vw,2.8rem)] tracking-tight">
            <span className="hero-line block">Building</span>
            <span className="hero-line text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">Digital Excellence</span>
          </h1>
          <p className="text-text-secondary text-sm leading-relaxed !pt-4">
            Since 2016, we have been delivering innovative and scalable digital solutions that empower businesses to adapt, grow, and succeed in an evolving technology landscape.
            Through continuous innovation, strategic execution, and modern technologies, we help organizations achieve digital excellence, improve performance, and build strong
            foundations for long term sustainable success in a competitive global market.
          </p>
        </div>

        <div className="relative !px-6 !py-8">
          <div className="grid grid-cols-2 gap-3">
            {STATS.map((item, i) => (
              <StatCard key={i} item={item} extraClass="!px-2 !py-1 !rounded" />
            ))}
          </div>
        </div>
      </div>

      <div className="hidden md:block lg:hidden" style={{ position: "relative", zIndex: 3 }}>
        <div className="flex flex-col justify-center !px-10 sm:!px-12" style={{ minHeight: "clamp(380px, 50vw, 560px)" }}>
          <div className="max-w-[55%]">
            <div className="flex items-center gap-4 mb-4">
              <h3 className="text-xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 !mt-4">
                About Us
              </h3>
              <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 rounded-full" />
            </div>
            <h3 className="font-display font-bold leading-[1.15] text-text-primary mb-4 text-[clamp(2rem,4.5vw,3.2rem)] tracking-tight !py-2">
              <span className="hero-line block">Building</span>
              <span className="hero-line text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">Digital Excellence</span>
            </h3>
            <p className="text-text-secondary text-sm sm:text-base leading-relaxed mb-8 !mt-2">
              Since 2016, we have been delivering innovative and scalable digital solutions that empower businesses to adapt, grow, and succeed in an evolving technology landscape.
              Through continuous innovation, strategic execution, and modern technologies, we help organizations achieve digital excellence, improve performance, and build strong
              foundations for long term sustainable success in a competitive global market.
            </p>
            <div className="grid grid-cols-2 gap-3 max-w-md !mt-4">
              {STATS.map((item, i) => (
                <StatCard key={i} item={item} extraClass="!px-2 !py-1 !rounded-md" />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block xl:hidden" style={{ position: "relative", zIndex: 3 }}>
        <div className="flex flex-col justify-center !px-8" style={{ minHeight: "clamp(420px, 56vw, 640px)" }}>
          <div className="w-[60%]">
            <div className="flex items-center gap-4 mb-4">
              <h3 className="text-xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">
                About Us
              </h3>
              <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 rounded-full" />
            </div>
            <h3 className="font-display font-bold leading-[1.15] text-text-primary mb-6 text-[clamp(3rem,3.5vw,4rem)] tracking-tight !py-4">
              <span className="hero-line block">Building</span>
              <span className="hero-line text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">Digital Excellence</span>
            </h3>
            <p className="text-text-secondary text-base leading-relaxed mb-10 max-w-[42vw] !pb-8">
              Since 2016, we have been delivering innovative and scalable digital solutions that empower businesses to adapt, grow, and succeed in an evolving technology landscape.
              Through continuous innovation, strategic execution, and modern technologies, we help organizations achieve digital excellence, improve performance, and build strong
              foundations for long term sustainable success in a competitive global market.
            </p>
            <div className="grid grid-cols-4 gap-3 max-w-2xl">
              {STATS.map((item, i) => (
                <StatCard key={i} item={item} extraClass="!px-2 !py-2 !rounded-md" />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="hidden xl:block" style={{ position: "relative", zIndex: 3 }}>
        <div className="flex flex-col justify-start py-10! px-10!" style={{ minHeight: "clamp(420px, 56vw, 80vh)" }}>
          <div className="max-w-7xl w-[65%]">
            <div className="flex items-center gap-4 ">
              <h3 className="text-2xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 !py-4">
                About Us
              </h3>
              <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 rounded-full" />
            </div>
            <h3 className="font-display font-bold leading-[1.15] text-text-primary mb-6 text-[clamp(2rem,2.9vw,4rem)] tracking-tight !pt-4 !pb-6">
              <span className="hero-line block">Building</span>
              <span className="hero-line text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">Digital Excellence</span>
            </h3>
            <p className="text-text-secondary text-[16px] 2xl:text-lg max-w-[40vw] leading-relaxed !mb-10">
              Since 2016, we have been delivering innovative and scalable digital solutions that empower businesses to adapt, grow, and succeed in an evolving technology landscape.
              Through continuous innovation, strategic execution, and modern technologies, we help organizations achieve digital excellence, improve performance, and build strong
              foundations for long term sustainable success in a competitive global market.
            </p>
            <div className="grid grid-cols-4 gap-4 mt-8 max-w-3xl">
              {STATS.map((item, i) => (
                <StatCard key={i} item={item} extraClass="lg:w-[180px] !px-4 !py-2 !rounded-md" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUsHero;
